import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
  getInputClassName,
  getTriggerClassName,
} from "../../field-ui";
import { useAnchoredOverlay } from "../../../../hooks/useAnchoredOverlay";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import type { ChoiceRow } from "../../../../lib/forms/fieldOptions";
import { labeledChoiceFromRows } from "../../../../lib/forms/fieldOptions";
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";
import ChoiceElement from "./choice-element";

interface ChoiceInputProps extends FormFieldInputProps {
  placeholder: string;
  labelMuted?: boolean;
  className?: string;
  footertext?: string;
  withCustom?: boolean;
  choiceRows: ChoiceRow[];
  disabled?: boolean;
  emptyHint?: string;
  validation?: (value: string) => boolean;
  /** Список в потоке документа — для чата, без portal. */
  inlineList?: boolean;
}

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    className={`h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    viewBox="0 0 14 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M13 7L7 0.999999L1 7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChoiceInput = forwardRef<FormFieldRef, ChoiceInputProps>((props, ref) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [value, setValue] = useState(props.initialValue ?? "");
  const [validationState, setValidationState] = useState(true);
  const inlineList = Boolean(props.inlineList);
  const [isOpened, setOpened] = useState(inlineList);
  const [customValue, setCustomValue] = useState(() => {
    const initial = props.initialValue ?? "";
    return props.withCustom &&
      !props.choiceRows.some((row) => row.value === initial)
      ? initial
      : "";
  });

  const selectedLabel =
    props.choiceRows.find((row) => row.value === value)?.label ?? value;

  const commitValue = (next: string) => {
    setValue(next);
    reportFieldValue(props.fieldName, next, props.onValueChange);
  };

  useEffect(() => {
    if (
      value &&
      !props.withCustom &&
      props.choiceRows.length > 0 &&
      !props.choiceRows.some((row) => row.value === value)
    ) {
      commitValue("");
      setCustomValue("");
      setOpened(false);
      setValidationState(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when options/value mismatch
  }, [props.choiceRows, props.withCustom, value]);

  const clear = () => {
    commitValue("");
    setCustomValue("");
  };

  const validate = () => {
    if (!props.validation) return true;
    const result = props.validation(value);
    setValidationState(result);
    return result;
  };

  useImperativeHandle(ref, () => ({
    setValidationState: setValidationState,
    value,
    scrollToElement: (options) => {
      shellRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        ...options,
      });
    },
    fieldId: props.fieldId,
    fieldName: props.fieldName,
    labeledChoice: labeledChoiceFromRows(props.choiceRows, value),
    validate,
    clear,
  }));

  const handleSelect = (row: ChoiceRow) => {
    commitValue(row.value);
    setValidationState(true);
    setCustomValue("");
    setOpened(false);
  };

  const showList = !props.disabled && (inlineList || isOpened);
  useClickOutside(
    containerRef,
    () => setOpened(false),
    showList && !inlineList,
    overlayRef,
  );
  useAnchoredOverlay({
    open: showList && !inlineList,
    triggerRef,
    overlayRef,
    minHeight: 160,
    matchTriggerWidth: true,
  });

  const list = showList ? (
    <div
      ref={inlineList ? undefined : overlayRef}
      id={listId}
      onMouseDown={(event) => {
        if ((event.target as HTMLElement).closest("input")) return;
        event.preventDefault();
      }}
      className={
        inlineList
          ? "mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          : "fixed z-[80] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-900/10"
      }
    >
      <div className="flex flex-col p-2">
        {props.withCustom ? (
          <div className="mb-2 border-b border-slate-100 pb-2">
            <p className="mb-2 px-1 text-xs font-medium text-slate-500 font-onest">
              Напишите, если нет в списке
            </p>
            <input
              value={customValue}
              onChange={(e) => {
                setCustomValue(e.target.value);
                commitValue(e.target.value);
                setValidationState(true);
              }}
              type="text"
              placeholder="Свой вариант"
              className={getInputClassName(true, "py-2.5 text-sm")}
            />
          </div>
        ) : null}
        <div className="scrollbar-thin max-h-56 overflow-y-auto">
          {props.choiceRows.length > 0 ? (
            props.choiceRows.map((row) => (
              <ChoiceElement
                key={`${row.value}-${row.label}`}
                title={row.label}
                handleClick={() => handleSelect(row)}
              />
            ))
          ) : (
            <p className="px-3 py-2.5 text-sm text-slate-500 font-onest">
              {props.emptyHint || "Нет вариантов"}
            </p>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <FieldShell ref={shellRef} className={props.className}>
      <FieldLabel muted={props.labelMuted}>{props.placeholder}</FieldLabel>
      <FieldControl>
        <div ref={containerRef} className="relative">
          <button
            ref={triggerRef}
            type="button"
            aria-expanded={showList}
            aria-invalid={!validationState}
            aria-controls={listId}
            disabled={props.disabled}
            onClick={() => {
              if (props.disabled || inlineList) return;
              setOpened((open) => !open);
            }}
            className={`${getTriggerClassName(validationState, Boolean(value))} ${
              props.disabled
                ? "cursor-not-allowed bg-slate-50 text-slate-400"
                : ""
            }`}
          >
            <span className="truncate">
              {selectedLabel || props.emptyHint || "Выберите вариант"}
            </span>
            <ChevronIcon open={showList} />
          </button>
          {inlineList
            ? list
            : list
              ? createPortal(list, document.body)
              : null}
        </div>
      </FieldControl>
      <FieldFooter>
        {!validationState ? (
          <span className="text-red-600" role="alert">
            Выберите значение
          </span>
        ) : props.footertext ? (
          <span className="text-slate-500">{props.footertext}</span>
        ) : null}
      </FieldFooter>
    </FieldShell>
  );
});

ChoiceInput.displayName = "ChoiceInput";

export { ChoiceInput };
export type { ChoiceInputProps };
